import { logger } from '@/utils/logger'
import Papa from 'papaparse'

// Constants for validation
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB
const MAX_ROWS = 1000000 // 1 million rows
const MAX_COLUMNS = 1000
const MAX_CELL_LENGTH = 10000
const REQUIRED_HEADERS = ['timestamp'] // Minimum required headers
const PRICE_HEADERS = ['price', 'value', 'close'] // Any of these can be used for price data

// CSV injection patterns to detect and sanitize
const CSV_INJECTION_PATTERNS = [
  /^[=@+\-]/,  // Formula injection
  /^[\t\r]/,   // Tab/carriage return injection
  /^<script/i, // Script injection
  /^javascript:/i, // JavaScript protocol
]

export interface CSVParseResult {
  data: any[]
  headers: string[]
  rowCount: number
  errors: string[]
  warnings: string[]
}

export interface CSVValidationOptions {
  requiredHeaders?: string[]
  maxFileSize?: number
  maxRows?: number
  maxColumns?: number
  allowEmptyRows?: boolean
  strictMode?: boolean
}

class CSVParseError extends Error {
  constructor(message: string, public details?: any) {
    super(message)
    this.name = 'CSVParseError'
  }
}

/**
 * Sanitizes a cell value to prevent CSV injection attacks
 */
function sanitizeCellValue(value: string): string {
  if (typeof value !== 'string') {
    return String(value)
  }

  // Trim whitespace
  value = value.trim()

  // Check for CSV injection patterns
  for (const pattern of CSV_INJECTION_PATTERNS) {
    if (pattern.test(value)) {
      // Prefix with single quote to neutralize formulas
      value = "'" + value
      logger.warn('Potential CSV injection detected and neutralized', { 
        originalValue: value.substring(0, 50) + '...' 
      })
    }
  }

  // Limit cell length
  if (value.length > MAX_CELL_LENGTH) {
    logger.warn(`Cell value truncated from ${value.length} to ${MAX_CELL_LENGTH} characters`)
    value = value.substring(0, MAX_CELL_LENGTH)
  }

  return value
}

/**
 * Parses various timestamp formats and returns a Unix timestamp in milliseconds
 * 
 * Supported formats:
 * - Unix timestamps (seconds or milliseconds)
 * - ISO dates: YYYY-MM-DD, YYYY/MM/DD
 * - European dates: DD-MM-YYYY, DD/MM/YYYY (prioritized for ambiguous cases)
 * - US dates: MM-DD-YYYY, MM/DD/YYYY (fallback for ambiguous cases)
 * - Standard Date.parse() compatible formats
 * 
 * For ambiguous dates (where both day and month are ≤ 12), European format
 * is prioritized to support international users. For example:
 * - "05-03-2025" → March 5, 2025 (European: DD-MM-YYYY)
 * - "25-12-2024" → December 25, 2024 (unambiguous European)
 * - "12-25-2024" → December 25, 2024 (unambiguous US)
 */
function parseTimestamp(value: string): number {
  const cleanValue = value.trim()

  // 1. Try localized date patterns FIRST (DD-MM-YYYY, MM-DD-YYYY, with optional time).
  //    Must run before Date.parse() which can silently normalize invalid times
  //    or misinterpret ambiguous month/day values.
  const datePattern = /^(\d{1,2})[-\/](\d{1,2})[-\/](\d{4})(?:[\sT](\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  const dateMatch = cleanValue.match(datePattern)
  if (dateMatch && dateMatch[1] && dateMatch[2] && dateMatch[3]) {
    const firstNum = parseInt(dateMatch[1])
    const secondNum = parseInt(dateMatch[2])
    const yearNum = parseInt(dateMatch[3])
    const hours = dateMatch[4] ? parseInt(dateMatch[4]) : undefined
    const minutes = dateMatch[5] ? parseInt(dateMatch[5]) : undefined
    const seconds = dateMatch[6] ? parseInt(dateMatch[6]) : undefined

    // Validate time component ranges
    if (hours !== undefined && (hours > 23 || (minutes ?? 0) > 59 || (seconds ?? 0) > 59)) {
      return NaN
    }

    const h = hours ?? 0
    const m = minutes ?? 0
    const s = seconds ?? 0

    // If first number > 12, it must be DD-MM-YYYY (European)
    if (firstNum > 12) {
      const date = new Date(yearNum, secondNum - 1, firstNum, h, m, s)
      if (isValidDate(date, yearNum, secondNum - 1, firstNum)) {
        return date.getTime()
      }
    }
    // If second number > 12, it must be MM-DD-YYYY (US)
    else if (secondNum > 12) {
      const date = new Date(yearNum, firstNum - 1, secondNum, h, m, s)
      if (isValidDate(date, yearNum, firstNum - 1, secondNum)) {
        return date.getTime()
      }
    }
    // Ambiguous case (both <= 12) - try European format first (DD-MM-YYYY)
    else {
      const europeanDate = new Date(yearNum, secondNum - 1, firstNum, h, m, s)
      if (isValidDate(europeanDate, yearNum, secondNum - 1, firstNum)) {
        return europeanDate.getTime()
      }

      const usDate = new Date(yearNum, firstNum - 1, secondNum, h, m, s)
      if (isValidDate(usDate, yearNum, firstNum - 1, secondNum)) {
        return usDate.getTime()
      }
    }
  }

  // 2. Try Date.parse() for ISO and other standard formats
  const timestamp = Date.parse(value)
  if (!isNaN(timestamp)) {
    return timestamp
  }

  // 3. Try YYYY-MM-DD format (ISO date without time)
  const isoDatePattern = /^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})$/
  const isoMatch = cleanValue.match(isoDatePattern)
  if (isoMatch && isoMatch[1] && isoMatch[2] && isoMatch[3]) {
    const year = parseInt(isoMatch[1])
    const month = parseInt(isoMatch[2]) - 1
    const day = parseInt(isoMatch[3])
    const date = new Date(year, month, day)

    if (isValidDate(date, year, month, day)) {
      return date.getTime()
    }
  }

  return NaN
}

/**
 * Validates that a constructed Date object matches the expected components
 */
function isValidDate(date: Date, year: number, month: number, day: number): boolean {
  return date.getFullYear() === year && 
         date.getMonth() === month && 
         date.getDate() === day
}

/**
 * Validates CSV headers
 */
function validateHeaders(
  headers: string[], 
  options: CSVValidationOptions
): { valid: boolean; errors: string[] } {
  const errors: string[] = []

  // Check for empty headers
  if (!headers || headers.length === 0) {
    errors.push('No headers found in CSV file')
    return { valid: false, errors }
  }

  // Check for duplicate headers
  const headerSet = new Set<string>()
  const duplicates: string[] = []
  
  headers.forEach(header => {
    const normalized = header.trim().toLowerCase()
    if (headerSet.has(normalized)) {
      duplicates.push(header)
    }
    headerSet.add(normalized)
  })

  if (duplicates.length > 0) {
    errors.push(`Duplicate headers found: ${duplicates.join(', ')}`)
  }

  // Check column count
  if (headers.length > (options.maxColumns || MAX_COLUMNS)) {
    errors.push(`Too many columns: ${headers.length} (max: ${options.maxColumns || MAX_COLUMNS})`)
  }

  // Check required headers (support aliases for timestamp)
  const requiredHeaders = options.requiredHeaders || REQUIRED_HEADERS
  let missingHeaders = requiredHeaders.filter(
    required => !headers.some(h => h.toLowerCase() === required.toLowerCase())
  )

  // Allow timestamp aliases that candle-converter's KEY_ALIASES also recognizes
  if (missingHeaders.includes('timestamp')) {
    const TIMESTAMP_ALIASES = ['time', 'date', 'ts_event']
    const hasAlias = headers.some(h => {
      const lower = h.toLowerCase().trim()
      return TIMESTAMP_ALIASES.includes(lower) || lower.endsWith(' time')
    })
    if (hasAlias) {
      missingHeaders = missingHeaders.filter(h => h.toLowerCase() !== 'timestamp')
    }
  }

  if (missingHeaders.length > 0) {
    errors.push(`Missing required headers: ${missingHeaders.join(', ')}`)
  }

  // Validate header names
  headers.forEach((header, index) => {
    if (!header || header.trim() === '') {
      errors.push(`Empty header at column ${index + 1}`)
    }
    
    // Check for suspicious header names
    if (CSV_INJECTION_PATTERNS.some(pattern => pattern.test(header))) {
      errors.push(`Suspicious header name at column ${index + 1}: "${header}"`)
    }
  })

  return { valid: errors.length === 0, errors }
}

/**
 * Validates a data row
 */
function validateRow(
  row: any,
  rowIndex: number,
  headers: string[],
  options: CSVValidationOptions
): { valid: boolean; errors: string[]; sanitizedRow: any } {
  const errors: string[] = []
  const sanitizedRow: any = {}

  // Check if row is empty
  const values = Object.values(row)
  const nonEmptyValues = values.filter(v => v !== null && v !== undefined && String(v).trim() !== '')
  
  if (nonEmptyValues.length === 0) {
    if (!options.allowEmptyRows) {
      errors.push(`Empty row at line ${rowIndex + 2}`)
      return { valid: false, errors, sanitizedRow }
    }
    return { valid: true, errors: [], sanitizedRow: row }
  }

  // Validate and sanitize each cell
  headers.forEach(header => {
    const value = row[header]
    
    if (value === null || value === undefined) {
      sanitizedRow[header] = ''
      return
    }

    const stringValue = String(value)
    
    // Sanitize the value
    sanitizedRow[header] = sanitizeCellValue(stringValue)

    // Additional validation for specific fields
    const lowerHeader = header.toLowerCase()
    const TIMESTAMP_HEADERS = ['timestamp', 'time', 'date', 'ts_event']
    if (TIMESTAMP_HEADERS.includes(lowerHeader) || lowerHeader.endsWith(' time')) {
      // Validate timestamp format - support Unix timestamps, ISO strings, and common date formats
      let timestamp: number
      
      // Check if it's a Unix timestamp (all digits)
      if (/^\d+$/.test(stringValue)) {
        // Unix timestamp - could be seconds or milliseconds
        timestamp = parseInt(stringValue, 10)
        // If it's a reasonable Unix timestamp in seconds (between 2000 and 2100), convert to ms
        if (timestamp > 946684800 && timestamp < 4102444800) {
          timestamp = timestamp * 1000
        }
      } else {
        // Try parsing various date formats
        timestamp = parseTimestamp(stringValue)
      }
      
      if (isNaN(timestamp) || timestamp < 0) {
        errors.push(`Invalid timestamp at row ${rowIndex + 2}: "${stringValue}". Supported formats: Unix timestamp, ISO date (YYYY-MM-DD), European date (DD-MM-YYYY), US date (MM/DD/YYYY)`)
      }
    } else if (PRICE_HEADERS.some(h => header.toLowerCase() === h || header.toLowerCase().includes(h))) {
      // Validate numeric fields
      const numValue = parseFloat(stringValue)
      if (isNaN(numValue)) {
        errors.push(`Invalid numeric value in ${header} at row ${rowIndex + 2}: "${stringValue}"`)
      } else if (!isFinite(numValue)) {
        errors.push(`Non-finite numeric value in ${header} at row ${rowIndex + 2}`)
      }
    }
  })

  return { valid: errors.length === 0, errors, sanitizedRow }
}

/**
 * Parses and validates a CSV file with security measures
 */
export async function parseCSVFile(
  file: File,
  options: CSVValidationOptions = {}
): Promise<CSVParseResult> {
  const errors: string[] = []
  const warnings: string[] = []

  logger.info('Starting CSV file parsing', {
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type
  })

  // Validate file size
  const maxSize = options.maxFileSize || MAX_FILE_SIZE
  if (file.size > maxSize) {
    throw new CSVParseError(
      `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size (${(maxSize / 1024 / 1024).toFixed(2)}MB)`
    )
  }

  // Validate file type
  if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
    warnings.push('File does not appear to be a CSV file based on extension/type')
  }

  return new Promise((resolve, reject) => {
    const results: any[] = []
    let headers: string[] = []
    let rowCount = 0
    const maxRows = options.maxRows || MAX_ROWS

    Papa.parse(file, {
      header: true,
      dynamicTyping: false, // Keep everything as strings for consistent validation
      skipEmptyLines: 'greedy',
      transformHeader: (header) => header.trim(),
      
      step: (row, parser) => {
        rowCount++

        // Check row limit
        if (rowCount > maxRows) {
          errors.push(`Row limit exceeded (max: ${maxRows})`)
          parser.abort()
          return
        }

        // Get headers on first row
        if (rowCount === 1) {
          headers = Object.keys(row.data as object)
          const headerValidation = validateHeaders(headers, options)
          
          if (!headerValidation.valid) {
            errors.push(...headerValidation.errors)
            if (options.strictMode) {
              parser.abort()
              return
            }
          }
        }

        // Validate and sanitize row
        const rowValidation = validateRow(row.data, rowCount - 1, headers, options)
        
        if (!rowValidation.valid) {
          errors.push(...rowValidation.errors)
          if (options.strictMode) {
            parser.abort()
            return
          }
        }

        // Add sanitized row to results
        results.push(rowValidation.sanitizedRow)
      },

      error: (error) => {
        logger.error('Papa Parse error', error)
        errors.push(`CSV parsing error: ${error.message}`)
      },

      complete: () => {
        // Final validation
        if (results.length === 0) {
          errors.push('No data rows found in CSV file')
        }

        // Check for consistent column count
        if (results.length > 0) {
          const expectedColumns = headers.length
          const inconsistentRows = results
            .map((row, index) => ({ row, index }))
            .filter(({ row }) => Object.keys(row).length !== expectedColumns)

          if (inconsistentRows.length > 0) {
            warnings.push(
              `${inconsistentRows.length} rows have inconsistent column counts`
            )
          }
        }

        logger.info('CSV parsing complete', {
          rowCount: results.length,
          headerCount: headers.length,
          errorCount: errors.length,
          warningCount: warnings.length
        })

        if (errors.length > 0 && options.strictMode) {
          reject(new CSVParseError('CSV validation failed', { errors, warnings }))
        } else {
          resolve({
            data: results,
            headers,
            rowCount: results.length,
            errors,
            warnings
          })
        }
      }
    })
  })
}

/**
 * Validates CSV data structure for optimization
 */
export function validateOptimizationData(data: any[]): { 
  valid: boolean; 
  errors: string[] 
} {
  const errors: string[] = []

  if (!Array.isArray(data) || data.length === 0) {
    errors.push('Data must be a non-empty array')
    return { valid: false, errors }
  }

  // Check first row for required fields
  const firstRow = data[0]
  if (!firstRow.timestamp) {
    errors.push('Data must contain a "timestamp" field')
  }
  
  // Check for any price-like field
  const hasPriceField = PRICE_HEADERS.some(field => 
    firstRow.hasOwnProperty(field) || 
    Object.keys(firstRow).some(key => key.toLowerCase().includes(field))
  )
  
  if (!hasPriceField) {
    errors.push('Data must contain a price field ("price", "value", or "close")')
  }

  // Validate data types and ranges
  data.forEach((row, index) => {
    // Validate timestamp
    if (row.timestamp) {
      let timestamp: number
      const timestampStr = String(row.timestamp)
      
      // Check if it's a Unix timestamp (all digits)
      if (/^\d+$/.test(timestampStr)) {
        // Unix timestamp - could be seconds or milliseconds
        timestamp = parseInt(timestampStr, 10)
        // If it's a reasonable Unix timestamp in seconds (between 2000 and 2100), convert to ms
        if (timestamp > 946684800 && timestamp < 4102444800) {
          timestamp = timestamp * 1000
        }
      } else {
        // Try to parse as date string
        timestamp = Date.parse(timestampStr)
      }
      
      if (isNaN(timestamp) || timestamp < 0) {
        errors.push(`Invalid timestamp at row ${index + 1}: ${row.timestamp}`)
      }
    }

    // Validate numeric fields - check all possible price fields
    let priceField: any
    for (const field of PRICE_HEADERS) {
      if (row[field] !== undefined) {
        priceField = row[field]
        break
      }
      // Also check for fields that contain the price header name
      const matchingKey = Object.keys(row).find(key => 
        key.toLowerCase().includes(field)
      )
      if (matchingKey && row[matchingKey] !== undefined) {
        priceField = row[matchingKey]
        break
      }
    }
    
    if (priceField !== undefined) {
      const numValue = parseFloat(priceField)
      if (isNaN(numValue)) {
        errors.push(`Invalid numeric value at row ${index + 1}: ${priceField}`)
      } else if (!isFinite(numValue)) {
        errors.push(`Non-finite numeric value at row ${index + 1}`)
      } else if (numValue < 0) {
        errors.push(`Negative price/value at row ${index + 1}: ${numValue}`)
      }
    }
  })

  // Check for chronological order
  for (let i = 1; i < data.length; i++) {
    let prevTime: number
    let currTime: number
    
    const prevTimestampStr = String(data[i - 1].timestamp)
    const currTimestampStr = String(data[i].timestamp)
    
    // Parse previous timestamp
    if (/^\d+$/.test(prevTimestampStr)) {
      prevTime = parseInt(prevTimestampStr, 10)
      if (prevTime > 946684800 && prevTime < 4102444800) {
        prevTime = prevTime * 1000
      }
    } else {
      prevTime = Date.parse(prevTimestampStr)
    }
    
    // Parse current timestamp
    if (/^\d+$/.test(currTimestampStr)) {
      currTime = parseInt(currTimestampStr, 10)
      if (currTime > 946684800 && currTime < 4102444800) {
        currTime = currTime * 1000
      }
    } else {
      currTime = Date.parse(currTimestampStr)
    }
    
    if (!isNaN(prevTime) && !isNaN(currTime) && prevTime > currTime) {
      errors.push(`Timestamps not in chronological order at row ${i + 1}`)
      break
    }
  }

  return { valid: errors.length === 0, errors }
}